import { useFormik, FormikProvider } from 'formik';
import InputFormik from '../../../../common/components/form/InputFormik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToastContext } from '../../../../common/components/ui/toast/toast.context';
import ComboBoxFormik from '../../../../common/components/form/ComboBoxFormik';
import { numberToCurrency } from '../../../../common/helpers/currency.helpers';
import ButtonWithLoader from '../../../../common/components/ui/loading/ButtonLoading';
import { asaasMaintenance } from '../../../../common/services/maintenance.api';
import { FaCheckCircle } from 'react-icons/fa';
import { maskCurrencyFieldAsaas } from '../../../../common/helpers/mask.helpers';
import { Budgetlabel, MAINTENANCE_STATUS } from '../../../../types/maintenance.type';

type Props = {
  renter: { _id: string, name: string }
  maintenanceId: string;
  licensePlate: string;
  amountMaintenance: number;
  dateDue: Date;
  typePlanRenter: string;
  onFormSubmitSuccess: () => void;
  itens: Budgetlabel[]
  disabled?: boolean;
  disabledMessage?: string;
}

type InstallmentOption = {
  value: string;
  label: string;
};

function FormAsaasPaymentMaintenance(props: Props) {
  const [paymentMethod, setPaymentMethod] = useState('-');
  const [deposit, setDeposit] = useState(0);
  const { notifySuccess, notifyError } = useToastContext();
  const [selectedInstallmentOption, setSelectedInstallmentOption] = useState('0');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [amountMaintenance, setAmountMaintenance] = useState(props.amountMaintenance);
  const [paidMaintenance, setPaidMaintenance] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePaymentMethodChange = useCallback((selectedOption: any) => {
    setPaymentMethod(selectedOption);
  }, []);

  const handleInstallmentOptionChange = useCallback((option) => {
    setSelectedInstallmentOption(option);
  }, []);

  const initialValues = {
    renterId: props.renter._id,
    depositInstallments: '0',
    amountMaintenance: 0,
    dueDate: new Date(),
    name: '',
    cpf: '',
    whatsapp: ''
  }

  const badgeColor = useMemo(() =>
    (paidMaintenance)
      ? 'text-greenPrimary'
      : 'text-redPrimary'
    , [paidMaintenance])

  const formik = useFormik({
    initialValues,
    onSubmit: async (data) => {
      if (props.disabled) {
        notifyError(
          props.disabledMessage ||
            "Revise o orçamento antes de gerar a cobrança."
        );
        return;
      }

      if (isSubmitting) return;
      setIsSubmitting(true);

      // Extrai os nomes das peças de uso indevido e corretivas
      const itensNames = props.itens.map(piece => piece.name).join(', ').trim();

      try {
        const activationData = {
          _id: props.maintenanceId,
          renterId: data.renterId,
          status: MAINTENANCE_STATUS.Finalize,
          name: data.name,
          cpf: data.cpf,
          whatsapp: data.whatsapp,
          asaasMaintenanceInfo: {
            paymentMethod: paymentMethod,
            deposit: deposit,
            dueDate: data.dueDate,
            reference: 'maintenance',
            description: `COBRANÇA DA MANUTENÇÃO DO VEÍCULO: ${props.licensePlate} \n - REFERENTE ÀS PEÇAS: \n ${itensNames} EM ${selectedInstallmentOption}X`,
            selectedInstallmentOption: selectedInstallmentOption,
            depositInstallments: amountMaintenance
          }
        }
        const result = await asaasMaintenance(activationData);
        if (!result.ok) {
          notifyError(result.message as string);
          return;
        }

        setPaidMaintenance(true);
        props.onFormSubmitSuccess();
        notifySuccess("Cobranças no Assas geradas com sucesso!");
      } catch (error) {
        notifyError("Erro ao processar a solicitação.");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const updateFormikValues = useCallback(() => {
    const maskedAmount = maskCurrencyFieldAsaas(props.amountMaintenance);
    if (formik.values.depositInstallments !== maskedAmount) {
      formik.setValues({
        ...formik.values,
        depositInstallments: maskedAmount
      });
    }
  }, [formik, props.amountMaintenance]);
  useEffect(() => {
    if (props.amountMaintenance === 0) return;

    updateFormikValues();
    setAmountMaintenance(props.amountMaintenance);
    setDeposit(props.amountMaintenance);
  }, [props.amountMaintenance, updateFormikValues]);

  const paymentOptions = [
    { value: "-", label: "-" },
    { value: "pix", label: "PIX" },
  ];

  const installmentOptions = useMemo<InstallmentOption[]>(() => {
    if (deposit === 0) return [{ value: "-", label: "-" }];

    const totalValue = Math.abs(deposit);  // Garantindo que o valor seja positivo para cálculos
    const options: InstallmentOption[] = [
      { value: "-", label: "-" }
    ];

    // Sempre adiciona a opção de pagamento único se o valor total for menor que 100
    if (totalValue < 100) {
      options.push({
        value: "1",
        label: `1 x R$ ${numberToCurrency(totalValue)}`
      });
    } else {
      // Se o valor total for maior ou igual a 100, calcula as parcelas possíveis
      for (let i = 1; i <= 12; i++) {
        const installmentValue = totalValue / i;
        if (installmentValue >= 50) {
          options.push({
            value: i.toString(),
            label: `${i} x R$ ${numberToCurrency(installmentValue.toFixed(2))}`
          });
        } else {
          break; // Se a parcela calculada é menor que 100, para o loop
        }
      }
    }

    return options;
  }, [deposit]);  // Dependência do useMemo

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="max-w-4xl mx-auto">
          <div className='mt-3'>
            <InputFormik
              name="depositInstallments"
              label="Valor da manutenção"
              required={true}
              readonly={true}
            />
          </div>
          {amountMaintenance !== 0 && (
            <ComboBoxFormik
              name="reserveDepositInstallments"
              label="Escolha o método de pagamento:"
              options={paymentOptions}
              required={true}
              onChange={handlePaymentMethodChange}
            />
          )}
          {paymentMethod && (
            <div>
              <ComboBoxFormik
                name="installmentOption"
                label="Escolha a opção de parcelamento:"
                options={installmentOptions}
                required={true}
                onChange={handleInstallmentOptionChange}
              />
            </div>
          )}
          <div>
            <InputFormik
              label="Data de vencimento:"
              name="dueDate"
              type="date"
              required={true}
            />
          </div>
        </section>
        <div className="grid grid-cols-2 items-center justify-center">
          <ButtonWithLoader
            type='button'
            isLoading={formik.isSubmitting || isSubmitting}
            onClick={formik.handleSubmit}
            disabled={paidMaintenance || amountMaintenance == 0 || props.disabled}
            message={props.disabledMessage}
          >
            Gerar Cobrança e Finalizar
          </ButtonWithLoader>
          <FaCheckCircle size={24} className={`ml-4 ${badgeColor}`} />
        </div>
        {props.disabledMessage && (
          <p className="mt-2 text-sm text-red-600">{props.disabledMessage}</p>
        )}
      </FormikProvider>
    </form>
  );
}

export default FormAsaasPaymentMaintenance;
